import React from "react";
import { css, Theme, useTheme } from "@emotion/react";

import { useAppState } from "core/contexts/AppContext";
import { localeCurrency } from "core/includes/formatters";
import { taxLabelWithTaxable } from "core/includes/finance";
import { useCountryAndCurrency } from "core/hooks/useCountryAndCurrency";
import useCart from "pages/Components/CartPage/hooks/useCart";

type Props = {
    quantity: number,
    discountPrice: number,
    isTaxable: boolean,
    totalPrice: number
};

const CartItemQuantityAndPrice: React.FC<Props> = ({ quantity, totalPrice, isTaxable, discountPrice }) => {
    const theme: Theme = useTheme();
    const [appState] = useAppState();
    const { getCountry } = useCart();
    const countryCode = getCountry();
    const { currencySymbol } = useCountryAndCurrency(countryCode.code);
    const wasDiscounted = discountPrice > 0;
    const discountedPrice = totalPrice - discountPrice;

    const style = css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        margin-left: 6px;
        height: 100%;
        max-height: 67px;
        white-space: nowrap;
        text-align: right;
        color: ${theme.colours.shipGray};

        ${theme.breakpoints.between("md", "xl")} {
            margin-left: 5px;
        }

        ${theme.breakpoints.between("sm", "lg")} {
            margin-left: 3px;
        }

        ${theme.breakpoints.down("xs")} {
            margin-left: 2px;
        }
    `;

    const quantityStyle = css`
        font-weight: ${theme.fonts.weights.bold};
    `;

    const gstStyle = css`
        font-size: 8px;
        line-height: 5px;
    `;

    const slashedPriceStyle = css`
        font-weight: ${theme.fonts.weights.light};
        text-decoration: line-through ${theme.colours.red1};
    `;

    return (
        <div css={style}>
            <div css={quantityStyle}>(Qty : {quantity})</div>
            <div>
                {wasDiscounted && (
                    <p css={slashedPriceStyle}>
                        {localeCurrency(totalPrice, currencySymbol)}
                    </p>
                )}
                <p>
                    {localeCurrency(wasDiscounted ? discountedPrice : totalPrice, currencySymbol)}
                </p>
                {!appState.globalCurrencyOptions.isGlobal && (
                    <p css={gstStyle}>({taxLabelWithTaxable(isTaxable, countryCode.taxLabel)})</p>
                )}
            </div>
        </div>
    );
};

export default CartItemQuantityAndPrice;